import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { FaCopy, FaCheck, FaTrash, FaChartBar, FaCog } from 'react-icons/fa';
import { ClientLocal } from '../../../../types/client.types';
import { formatCurrency } from '../../../../../../../../utils/functions';
import { toast } from 'react-toastify';
import RemoveClient from '../RemoveClient/RemoveClient';
import ClientDetailsModal from '../ClientDetailsModal/ClientDetailsModal';
import './ClientsTable.scss';

interface ClientsTableProps {
  clients: ClientLocal[];
  onRefresh: () => void;
}

const ClientsTable: React.FC<ClientsTableProps> = ({ clients, onRefresh }) => {
  const [copiedEmail, setCopiedEmail] = useState<number | null>(null);
  const [copiedPhone, setCopiedPhone] = useState<number | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientLocal | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = Number(user.local_id) || 0;

  const formatDate = (dateString?: string) => {
    if (!dateString) return <div className="table-na-value">N/A</div>;
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return <div className="table-na-value">N/A</div>;
    }
  };

  const getFullName = (client: ClientLocal) => {
    const parts = [client.username, client.middle_name, client.last_name].filter(Boolean);
    return parts.join(' ') || 'N/A';
  };

  const copyToClipboard = async (text: string, clientId: number, type: 'email' | 'phone') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'email') {
        setCopiedEmail(clientId);
        setTimeout(() => setCopiedEmail(null), 2000);
      } else {
        setCopiedPhone(clientId);
        setTimeout(() => setCopiedPhone(null), 2000);
      }
      toast.success('Copiado al portapapeles');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const handleRemoveClick = (client: ClientLocal) => {
    setSelectedClient(client);
    setShowRemoveModal(true);
  };

  const handleDetailsClick = (client: ClientLocal) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleRemoveSuccess = () => {
    setShowRemoveModal(false);
    setSelectedClient(null);
    onRefresh();
  };

  const handleRemoveClose = () => {
    setShowRemoveModal(false);
    setSelectedClient(null);
  };

  const handleDetailsClose = () => {
    setShowDetailsModal(false);
    setSelectedClient(null);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="clients-table-container custom-table-div">
      <div className="scroll-container-table">
        <table className="custom-table">
          <thead>
            <tr>
              <th className="no_pagination">Cliente</th>
              <th className="no_pagination no-responsive">Email</th>
              <th className="no_pagination no-responsive">Teléfono</th>
              <th className="no_pagination">Membresía</th>
              <th className="no_pagination">Visitas</th>
              <th className="no_pagination no-responsive">Total Gastado</th>
              <th className="no_pagination">Última Visita</th>
              <th className="no_pagination">{isMobile ? <FaCog /> : 'Acciones'}</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.client_id}>
                <td>
                  <div className="table-cell-client-name">
                    <span>{getFullName(client)}</span>
                  </div>
                </td>
                <td className="no-responsive">
                  {client.email ? (
                    <div className="table-cell-with-copy">
                      <span>{client.email}</span>
                      <button
                        className="copy-icon-button"
                        onClick={() => copyToClipboard(client.email!, client.client_id, 'email')}
                        title="Copiar email"
                      >
                        {copiedEmail === client.client_id ? (
                          <FaCheck className="check-icon" />
                        ) : (
                          <FaCopy />
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="table-na-value">N/A</div>
                  )}
                </td>
                <td className="no-responsive">
                  {client.phone_number ? (
                    <div className="table-cell-with-copy">
                      <span>{client.phone_number}</span>
                      <button
                        className="copy-icon-button"
                        onClick={() => copyToClipboard(client.phone_number!, client.client_id, 'phone')}
                        title="Copiar teléfono"
                      >
                        {copiedPhone === client.client_id ? (
                          <FaCheck className="check-icon" />
                        ) : (
                          <FaCopy />
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="table-na-value">N/A</div>
                  )}
                </td>
                <td>
                  {client.membership ? (
                    <div className="table-cell-membership">
                      <span className="membership-name">{client.membership.membership_name}</span>
                      <span className="membership-discount">
                        {client.membership.discount_percentage}% desc.
                      </span>
                    </div>
                  ) : (
                    <div className="table-na-value">Sin membresía</div>
                  )}
                </td>
                <td>
                  <div className="table-cell-visits">{client.visits}</div>
                </td>
                <td className="no-responsive">
                  <div className="table-cell-total">
                    {formatCurrency(client.total_spent)}
                  </div>
                </td>
                <td>{formatDate(client.last_visit)}</td>
                <td>
                  <div className="table-cell-actions">
                    <button
                      className="action-button details-button"
                      onClick={() => handleDetailsClick(client)}
                      title="Ver detalles y estadísticas"
                    >
                      <FaChartBar />
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleRemoveClick(client)}
                      title="Eliminar cliente"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Remove Client Modal */}
      {showRemoveModal && selectedClient && (
        <RemoveClient
          show={showRemoveModal}
          handleClose={handleRemoveClose}
          onSuccess={handleRemoveSuccess}
          localId={localId}
          client={selectedClient}
        />
      )}

      {/* Client Details Modal */}
      {showDetailsModal && selectedClient && (
        <ClientDetailsModal
          show={showDetailsModal}
          handleClose={handleDetailsClose}
          client={selectedClient}
          localId={localId}
        />
      )}
    </div>
  );
};

export default ClientsTable;
